import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useLocations } from "@/hooks/useFirestore";
import { useRequireSelectedOrgId } from "@/hooks/useRequireSelectedOrgId";
import { useDuplicateExperimentWithPlots } from "@/services/dataService";
import { Experiment } from "@/types/experiment";
import { Plot } from "@/types/plot";
import { Copy, Grid3X3, Info } from "lucide-react";
import React, { useEffect, useState } from "react";
import LocationSelector from "./LocationSelector";

interface DuplicateExperimentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trialId: string;
  sourceExperiment: Experiment;
  sourcePlots: Plot[];
  onExperimentDuplicated: (newExperiment: Experiment) => void;
  onDuplicateStart?: (experimentName: string, startedAtMs: number) => void;
  onDuplicateError?: (error: unknown) => void;
}

const DuplicateExperimentDialog: React.FC<DuplicateExperimentDialogProps> = ({
  open,
  onOpenChange,
  trialId,
  sourceExperiment,
  sourcePlots,
  onExperimentDuplicated,
  onDuplicateStart,
  onDuplicateError,
}) => {
  const { toast } = useToast();
  const { userRole } = useAuth();
  const requireOrgId = useRequireSelectedOrgId();
  const { locations, loading: locationsLoading } = useLocations();
  const duplicateExperimentWithPlots = useDuplicateExperimentWithPlots();

  const canAddLocation = userRole && ["editor", "admin"].includes(userRole);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState<{ progress: number; message: string } | null>(null);

  // Reset form when dialog opens or source changes
  useEffect(() => {
    if (open) {
      setName(`Copy of ${sourceExperiment.name}`);
      setDescription(sourceExperiment.description || "");
      setLocation(sourceExperiment.location || "");
    }
  }, [open, sourceExperiment]);

  // Compute summary info
  const plotCount = sourcePlots.length;
  const hasGrid = sourceExperiment.plotMap?.numRows && sourceExperiment.plotMap?.numCols;
  const metadataKeys = Object.keys(sourcePlots[0]?.metadata || {});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    // Capture the organization once so the experiment, its plot copies and any
    // rollback all target it even if the user switches organization mid-flight.
    const orgId = requireOrgId();
    if (!orgId) return;

    setIsSubmitting(true);
    setSubmissionProgress({ progress: 0, message: "Preparing..." });

    try {
      const newExperiment = await duplicateExperimentWithPlots(
        trialId,
        sourceExperiment,
        sourcePlots,
        { name: name.trim(), description, location },
        (progress, message) => {
          setSubmissionProgress({ progress, message });
        },
        { orgId }
      );

      if (newExperiment) {
        // Show transition overlay only after duplication completes (dialog has its own progress UI)
        onDuplicateStart?.(name.trim(), Date.now());
        onExperimentDuplicated(newExperiment);
        onOpenChange(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to duplicate experiment",
        variant: "destructive",
      });
      onDuplicateError?.(error);
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isSubmitting) onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Duplicate Experiment</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Create a copy of "{sourceExperiment.name}" with all its plots.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-y-auto px-6 pb-6">
          <div className="space-y-4 pt-4 flex-1">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="duplicate-name">
                Experiment Name <span className="text-muted-foreground">(required)</span>
              </Label>
              <Input
                id="duplicate-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter experiment name"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="duplicate-description">Description</Label>
              <Textarea
                id="duplicate-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter a brief description"
                rows={3}
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="duplicate-location">Location</Label>
              <LocationSelector
                locations={locations}
                locationsLoading={locationsLoading}
                selectedLocation={location}
                onSelectLocation={setLocation}
                canAddLocation={canAddLocation}
                placeholder="Select a location"
              />
            </div>

            {/* Plot Summary */}
            <div className="bg-muted/50 rounded-lg border p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Info className="h-4 w-4 text-muted-foreground" />
                Plots will be copied from the original experiment
              </div>
              <div className="space-y-1 text-sm text-muted-foreground pl-6">
                <div className="flex items-center gap-2">
                  <Copy className="h-3.5 w-3.5" />
                  <span>{plotCount.toLocaleString()} {plotCount === 1 ? "plot" : "plots"}</span>
                </div>
                {hasGrid && (
                  <div className="flex items-center gap-2">
                    <Grid3X3 className="h-3.5 w-3.5" />
                    <span>{sourceExperiment.plotMap!.numRows} rows x {sourceExperiment.plotMap!.numCols} columns</span>
                  </div>
                )}
                {metadataKeys.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>{metadataKeys.length} metadata {metadataKeys.length === 1 ? "field" : "fields"}: {metadataKeys.join(", ")}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Progress indicator */}
            {submissionProgress && (
              <div className="space-y-3 bg-muted/50 rounded-lg p-4 border">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium truncate">{submissionProgress.message}</span>
                      <span className="font-semibold text-primary ml-2">{Math.round(submissionProgress.progress * 100)}%</span>
                    </div>
                  </div>
                </div>
                <Progress value={submissionProgress.progress * 100} className="h-2" />
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={!name.trim() || isSubmitting}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Duplicating...
                </span>
              ) : (
                "Duplicate Experiment"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateExperimentDialog;
